import { merge } from 'lodash-es';
import {
  addressNoMilitaryUI,
  addressNoMilitarySchema,
  textUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import CaregiverCountyDescription from '../components/FormDescriptions/CaregiverCountyDescription';
import content from '../locales/en/content.json';
import { setAddressCountry } from '../utils/helpers';
import { validateCountyInput } from '../utils/validation';

/**
 * App-specific addressUI that extends platform addressNoMilitaryUI with an omitted
 * street3 field, a customizable street hint, and an added county field.
 *
 * Overrides from platform:
 * - Omits the street3 field from the address
 * - Hides the country field on the review page and sets its updateSchema to setAddressCountry
 * - Adds a hint option to the street address field
 * - Adds a county field (as textUI) with a title and hint from content.json, a description
 *   (CaregiverCountyDescription) shown only when the field is required, a conditional required
 *   flag, character validation, and a custom required error message
 *
 * @param {Object} [options={}] - Configuration options
 * @param {string|null} [options.streetHint=null] - Hint text displayed under the street field
 * @param {boolean} [options.requireCounty=true] - Whether the county field is required;
 *  also controls whether the county description is shown
 * @returns {UISchemaOptions} UI schema based on addressNoMilitaryUI (without street3) with
 *  the above overrides and an added county field
 *
 * @example
 * // Default usage: county required, no street hint
 * veteranAddress: addressUI()
 *
 * @example
 * // Make the county field optional (and hide its description) and add a street hint
 * primaryAddress: addressUI({
 *   requireCounty: false,
 *   streetHint: 'This address cannot be a PO Box',
 * })
 *
 * @see {@link https://va.ghe.com/software/vets-website/blob/main/src/platform/forms-system/src/js/web-component-patterns/addressPattern.jsx platform addressNoMilitaryUI}
 */
export const addressUI = ({ streetHint = null, requireCounty = true } = {}) =>
  merge({}, addressNoMilitaryUI({ omit: ['street3'] }), {
    country: {
      'ui:options': {
        hideOnReview: true,
        updateSchema: setAddressCountry,
      },
    },
    street: {
      'ui:options': { hint: streetHint },
    },
    county: textUI({
      title: content['form-address-county-label'],
      hint: content['form-address-county-hint'],
      description: requireCounty && CaregiverCountyDescription,
      required: () => requireCounty,
      validations: [validateCountyInput],
      errorMessages: {
        required: content['validation-address--county-required'],
        pattern: content['validation-address--county-pattern'],
      },
    }),
  });

/**
 * App-specific addressSchema that extends platform addressNoMilitarySchema with
 * street3 omitted and support for extending individual field schemas.
 *
 * Overrides from platform:
 * - Omits the street3 field from the address
 * - Passes through an extend object so callers can extend individual field
 *   schemas (e.g. adding maxLength), matching platform's extend behavior
 *
 * @param {Object} [options={}] - Configuration options
 * @param {Object} [options.extend] - Field-specific schema overrides, keyed by
 *  field name (e.g. { street: { maxLength: 30 } }), merged into the
 *  corresponding field schemas
 * @returns {SchemaOptions} Schema based on addressNoMilitarySchema (without street3)
 *  with the provided extend overrides applied
 *
 * @example
 * // Default usage, no overrides
 * address: addressSchema()
 *
 * @example
 * // Extend the street and city field schemas
 * address: addressSchema({
 *   extend: {
 *     street: { maxLength: 30 },
 *     city: { maxLength: 18 },
 *   },
 * })
 *
 * @see {@link https://va.ghe.com/software/vets-website/blob/main/src/platform/forms-system/src/js/web-component-patterns/addressPattern.jsx platform addressNoMilitarySchema}
 */
export const addressSchema = ({ extend = {} } = {}) =>
  merge({}, addressNoMilitarySchema({ omit: ['street3'] }), {
    properties: extend,
  });
